import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, CheckCircle2, FileText, MapPin, Receipt } from "lucide-react";
import { portalApi, getPortalClient } from "../../lib/portalApi";
import { brl } from "../../lib/format";
import { useI18n } from "../../i18n/I18nContext";
import { logError } from "../../lib/logger";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function StatusPill({ status }) {
  const { t } = useI18n();
  const map = {
    agendado: { label: "Agendado", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    em_andamento: { label: "Em andamento", cls: "bg-blue-50 text-blue-800 border-blue-200" },
    concluido: { label: "Concluído", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    cancelado: { label: "Cancelado", cls: "bg-rose-50 text-rose-800 border-rose-200" },
  };
  const it = map[status] || { label: status || "—", cls: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${it.cls}`}>
      {t(it.label)}
    </span>
  );
}

export default function PortalDashboard() {
  const { t } = useI18n();
  const client = getPortalClient();
  const [stats, setStats] = useState({ total: 0, concluidos: 0, proximos: 0, gasto: 0 });
  const [next, setNext] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: jobs } = await portalApi.get("/portal/jobs");
        const now = new Date();
        const total = jobs.length;
        const concluidos = jobs.filter((j) => j.status === "concluido");
        const proximos = jobs.filter((j) => new Date(j.scheduled_at) > now && j.status !== "cancelado");
        const gasto = concluidos.reduce((acc, j) => acc + (Number(j.value) || 0), 0);
        const upcoming = [...proximos].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
        setStats({ total, concluidos: concluidos.length, proximos: proximos.length, gasto });
        setNext(upcoming);
      } catch (e) {
        logError("Portal: falha ao carregar atendimentos", e);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          {t("Bem-vindo,")} <span className="text-blue-700">{client?.name?.split(" ")[0] || t("Cliente")}</span>!
        </h1>
        <p className="text-slate-600 mt-1 text-sm">
          {t("Aqui você acompanha sua agenda, recibos e orçamentos.")}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-stats">
        <StatCard icon={<Calendar size={18} />} label={t("Próximos serviços")} value={stats.proximos} accent="text-amber-700" />
        <StatCard icon={<CheckCircle2 size={18} />} label={t("Serviços concluídos")} value={stats.concluidos} accent="text-emerald-700" />
        <StatCard icon={<Receipt size={18} />} label={t("Total gasto")} value={brl(stats.gasto)} accent="text-blue-700" />
        <StatCard icon={<FileText size={18} />} label={t("Total serviços")} value={stats.total} accent="text-slate-700" />
      </div>

      {next ? (
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5" data-testid="portal-next-card">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-1">{t("Próximo serviço")}</div>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="text-xl font-bold text-slate-900">
                {formatDate(next.scheduled_at)}
              </div>
              <div className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
                <MapPin size={13} /> {next.address || client?.address || "—"}
              </div>
            </div>
            <StatusPill status={next.status} />
          </div>
          <div className="mt-3 flex gap-3">
            <Link to="/portal/agenda" className="text-sm text-blue-700 hover:underline" data-testid="portal-see-agenda">
              {t("Ver toda a agenda →")}
            </Link>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500 text-sm">
          {t("Você não tem nenhum serviço agendado no momento.")}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`flex items-center gap-2 text-sm ${accent}`}>
        {icon}
        <span className="font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-2">{value}</div>
    </div>
  );
}
